/**
 * The price rules (APP-ENTITLEMENT-001): every monthly unit price ends in .95 (Q-AE-14); annual is exactly ten
 * months (Q-AE-21, Q-AE-24); the card-processing rate is Stripe's published rate plus the platform's margin, every
 * term a console setting (Q-AE-17). No price is here — only what a price must obey.
 *
 * @module @puffin-core/app-catalog/rules/pricing
 */

'use strict';

const { ANNUAL_MONTHS_CHARGED, PRICE_CENTS_ENDING, PRICE_ENDING } = require('../vocabulary');
const { APP_CATALOG_REFUSAL, AppCatalogRefusal } = require('../refusal');

function assertCents(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new TypeError(
      `${name} must be a non-negative integer of cents, got ${JSON.stringify(value)}`,
    );
  }
}

/**
 * @param {number} unitAmountCents
 * @returns {boolean} true when the amount ends in .95.
 */
function endsInNinetyFive(unitAmountCents) {
  assertCents(unitAmountCents, 'unitAmountCents');
  return unitAmountCents % 100 === PRICE_CENTS_ENDING;
}

/**
 * A MONTHLY per-unit price must end in .95.
 *
 * @param {number} unitAmountCents
 * @returns {number} the amount, accepted.
 * @throws {AppCatalogRefusal} `PRICE_ENDING_REFUSED`.
 */
function assertPointNinetyFive(unitAmountCents) {
  if (!endsInNinetyFive(unitAmountCents)) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.PRICE_ENDING_REFUSED, { unitAmountCents });
  }
  return unitAmountCents;
}

/**
 * @param {number} unitAmountCents
 * @returns {boolean} true when the amount is whole dollars.
 */
function endsInWholeDollars(unitAmountCents) {
  assertCents(unitAmountCents, 'unitAmountCents');
  return unitAmountCents % 100 === 0;
}

/**
 * A MONTHLY unit price must end the way its dimension says (`PRICE_ENDING`): .95 for every dimension but one, whole
 * dollars for PayDayPuffin's employee paid (Q-AE-14, "$49.95 + $6 per employee paid").
 *
 * @param {{dimensionKey: string, priceEnding: string}} dimension - a catalog dimension.
 * @param {number} unitAmountCents
 * @returns {number} the amount, accepted.
 * @throws {AppCatalogRefusal} `PRICE_ENDING_REFUSED`, naming the dimension and the ending it wants.
 * @throws {TypeError} on a dimension whose ending is not in the vocabulary.
 */
function assertUnitPriceEnding(dimension, unitAmountCents) {
  const accepted = {
    [PRICE_ENDING.NINETY_FIVE]: endsInNinetyFive,
    [PRICE_ENDING.WHOLE_DOLLARS]: endsInWholeDollars,
  };
  const judge = Object.hasOwn(accepted, dimension.priceEnding)
    ? accepted[dimension.priceEnding]
    : null;
  if (judge === null) {
    throw new TypeError(
      `dimension ${dimension.dimensionKey} has no price ending (${JSON.stringify(dimension.priceEnding)})`,
    );
  }
  if (!judge(unitAmountCents)) {
    throw new AppCatalogRefusal(APP_CATALOG_REFUSAL.PRICE_ENDING_REFUSED, {
      unitAmountCents,
      dimensionKey: dimension.dimensionKey,
      priceEnding: dimension.priceEnding,
    });
  }
  return unitAmountCents;
}

/**
 * The annual amount for a monthly unit price: exactly ten months (two free). The .95 rule binds the monthly unit;
 * the annual figure is arithmetic the page shows as "10 × $129.95".
 *
 * @param {number} monthlyUnitAmountCents
 * @returns {number}
 */
function annualFromMonthly(monthlyUnitAmountCents) {
  assertCents(monthlyUnitAmountCents, 'monthlyUnitAmountCents');
  return monthlyUnitAmountCents * ANNUAL_MONTHS_CHARGED;
}

/**
 * The per-month equivalent of an annual amount, rounded to the cent, for the "billed annually" line.
 *
 * @param {number} annualAmountCents
 * @returns {number}
 */
function monthlyEquivalentOfAnnual(annualAmountCents) {
  assertCents(annualAmountCents, 'annualAmountCents');
  return Math.round(annualAmountCents / 12);
}

/**
 * The published card-processing rate: Stripe's published percent plus the platform margin, and Stripe's fixed
 * cents unchanged. Every input is a console setting.
 *
 * @param {{stripeRateBps: number, stripeFixedCents: number, marginBps: number}} params
 * @returns {{rateBps: number, fixedCents: number}}
 */
function processingRate({ stripeRateBps, stripeFixedCents, marginBps }) {
  for (const [name, value] of Object.entries({ stripeRateBps, stripeFixedCents, marginBps })) {
    if (!Number.isInteger(value) || value < 0) {
      throw new TypeError(`${name} must be a non-negative integer, got ${JSON.stringify(value)}`);
    }
  }
  return { rateBps: stripeRateBps + marginBps, fixedCents: stripeFixedCents };
}

module.exports = {
  endsInNinetyFive,
  endsInWholeDollars,
  assertPointNinetyFive,
  assertUnitPriceEnding,
  annualFromMonthly,
  monthlyEquivalentOfAnnual,
  processingRate,
};
